import { useState } from "react";

import { Circle, cn } from "@eisox/design-system";
import { RoomIcon, SparklesIcon, SunIcon, ThermometerIcon, WaterDropIcon, WindIcon } from "@eisox/icons";
import { DATA_SEUILS, getAirQualityThreshold, getAirQualityV2Threshold } from "@eisox/valves";
import { ParaglideMessage } from "@inlang/paraglide-js-react";

import { Help } from "~/components/help";
import { getGradrient } from "~/helpers/color.helper";
import { m } from "~/paraglide/messages";

import {
  airQualityGradient,
  airQualityGradientV2,
  getAirQualityColor,
  getHumidityColor,
  getLightColor,
  getTemperatureColor,
  humidityGradient,
  lightGradient,
  temperatureGradient,
} from "../helpers/sensor.helper";
import * as SensorCard from "./sensor-card";
import { Settings } from "./settings";

interface SensorsProps {
  temperature?: number;
  isTemperatureEstimated?: boolean;
  temperatureValveCount?: number;
  humidity?: number;
  light?: number;
  airQuality?: number;
  airQualityV2?: boolean;
  className?: string;
  canReadTemperature?: boolean;
  canReadHumidity?: boolean;
  canReadLight?: boolean;
  canReadAirQuality?: boolean;
}

const Sensors: React.FC<SensorsProps> = ({
  className,
  temperature,
  isTemperatureEstimated = false,
  temperatureValveCount = 0,
  humidity,
  light,
  airQuality,
  airQualityV2 = false,
  canReadHumidity = false,
  canReadLight = false,
  canReadAirQuality = false,
}) => {
  const [settingsOpen, setSettingsOpen] = useState(false);

  const temperatureColor = temperature !== undefined ? getTemperatureColor(temperature) : undefined;
  const temperatureJauge = getGradrient(temperatureGradient);

  const humidityColor = humidity !== undefined ? getHumidityColor(humidity) : undefined;
  const humidityJauge = getGradrient(humidityGradient);

  const airQualityColor = airQuality !== undefined ? getAirQualityColor(airQuality, airQualityV2) : undefined;
  const airQualityJauge = getGradrient(airQualityV2 ? airQualityGradientV2 : airQualityGradient);
  const airQualityThreshold =
    airQuality !== undefined
      ? airQualityV2
        ? getAirQualityV2Threshold(airQuality)
        : getAirQualityThreshold(airQuality)
      : undefined;

  const lightColor = light !== undefined ? getLightColor(light) : undefined;
  const lightJauge = getGradrient(lightGradient);

  return (
    <div className={cn("gap-2.5 flex flex-wrap", className)}>
      <SensorCard.Root>
        <SensorCard.Icon
          style={{ backgroundColor: temperatureColor?.background, color: temperatureColor?.color }}
          className="min-[1400px]:flex-col"
        >
          <ThermometerIcon />
          <p className="hidden text-[8px] min-[1400px]:block">{m.room()}</p>
          {isTemperatureEstimated && temperature !== undefined && (
            <Circle.Badge
              size={22}
              style={{ backgroundColor: temperatureColor?.background, color: temperatureColor?.color }}
              className="hidden min-[1400px]:flex"
            >
              <RoomIcon />
            </Circle.Badge>
          )}
        </SensorCard.Icon>
        <SensorCard.Group>
          <SensorCard.Value>
            {isTemperatureEstimated && temperature !== undefined && (
              <SparklesIcon
                aria-label={m.temperature_estimated()}
                className="mr-1 inline-block size-[0.9em] align-[-0.1em] text-primary-warning"
                fill="currentColor"
              />
            )}
            {temperature !== undefined ? temperature.toFixed(1) : "--"}
            <SensorCard.Unity>°C</SensorCard.Unity>
          </SensorCard.Value>
          <Help keepHoverOpen>
            <ParaglideMessage
              message={m.room_sensor_temperature_help}
              inputs={{ estimated: isTemperatureEstimated ? "true" : "false" }}
              markup={{
                button: ({ children }) => (
                  <button
                    className="cursor-pointer text-base text-primary-white italic underline"
                    onClick={() => setSettingsOpen(true)}
                  >
                    {children}
                  </button>
                ),
              }}
            />
            {temperatureValveCount > 0 && (
              <>
                {"\n"}
                {m.room_sensor_temperature_valve_count({ count: temperatureValveCount })}
              </>
            )}
          </Help>
        </SensorCard.Group>
        <SensorCard.Gradient
          maxText={m.hot()}
          minText={m.cold()}
          minValue={DATA_SEUILS.temperature.MIN - 3}
          maxValue={DATA_SEUILS.temperature.MAX + 3}
          value={temperature}
          gradient={temperatureJauge}
        />
      </SensorCard.Root>

      {canReadHumidity && (
        <SensorCard.Root>
          <SensorCard.Icon style={{ backgroundColor: humidityColor?.background, color: humidityColor?.color }}>
            <WaterDropIcon />
          </SensorCard.Icon>
          <SensorCard.Value>
            {humidity !== undefined ? humidity.toFixed(1) : "--"}
            <SensorCard.Unity>%</SensorCard.Unity>
          </SensorCard.Value>
          <SensorCard.Gradient
            maxText={m.wet()}
            minText={m.dry()}
            minValue={0}
            maxValue={100}
            value={humidity}
            gradient={humidityJauge}
          />
        </SensorCard.Root>
      )}

      {canReadAirQuality && airQuality !== undefined && (
        <SensorCard.Root>
          <SensorCard.Icon style={{ backgroundColor: airQualityColor?.background, color: airQualityColor?.color }}>
            <WindIcon />
          </SensorCard.Icon>
          <SensorCard.Value>
            {airQualityThreshold ? m.air_quality_values({ quality: airQualityThreshold }) : "--"}
          </SensorCard.Value>
          <SensorCard.Gradient
            maxText={m.excellent()}
            minText={m.bad()}
            minValue={airQualityV2 ? DATA_SEUILS.airQualityV2.MAX : DATA_SEUILS.airQuality.MAX}
            maxValue={airQualityV2 ? DATA_SEUILS.airQualityV2.MIN : DATA_SEUILS.airQuality.MIN}
            value={airQuality}
            gradient={airQualityJauge}
          />
        </SensorCard.Root>
      )}

      {canReadLight && (
        <SensorCard.Root>
          <SensorCard.Icon style={{ backgroundColor: lightColor?.background, color: lightColor?.color }}>
            <SunIcon />
          </SensorCard.Icon>
          <SensorCard.Value>
            {light !== undefined ? light.toFixed(1) : "--"}
            <SensorCard.Unity>lux</SensorCard.Unity>
          </SensorCard.Value>
          <SensorCard.Gradient
            maxText={m.bright()}
            minText={m.dark()}
            minValue={DATA_SEUILS.light.MIN - 200}
            maxValue={DATA_SEUILS.light.MAX + 200}
            value={light}
            gradient={lightJauge}
          />
        </SensorCard.Root>
      )}
      {settingsOpen && <Settings open onOpenChange={setSettingsOpen} value="calibration" />}
    </div>
  );
};

export { Sensors };
